import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const PAGE_SIZE = 100;
export const ORANGE_BTN = "rounded-sm gap-1.5 bg-[#FF5A00] hover:bg-[#e04f00]";

export const qty = (n, digits = 2) =>
  n == null || n === "" ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: digits });

export const money = (n) =>
  n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const unit = (uom) => (uom === "KG" ? "kg" : "m");

/** How many metres a job needs off a roll of a DIFFERENT width than planned, so it still gets the labels it
 * needs. The die is tooled for a fixed number across (ups_across) — a wider roll doesn't buy more of them,
 * it's just extra trim; only a narrower roll changes anything, by cutting how many of the planned ups fit.
 * Used wherever a "Required" figure is shown against an actual or prospective roll — the Issue dialog's own
 * preview and the BPR sheet itself (issued and still-pending rows alike), so both always agree. */
export function adjustedRequired(job, requiredM, actualWidthMm) {
  const plannedWidth = Number(job?.stock_width_mm), plannedUps = Number(job?.ups_across), req = Number(requiredM);
  if (!(plannedWidth > 0) || !(plannedUps > 0) || !(req > 0) || !(actualWidthMm > 0)) return null;
  const perUp = plannedWidth / plannedUps;
  const fitUps = Math.min(plannedUps, Math.floor(actualWidthMm / perUp));
  if (fitUps <= 0) return { fitUps: 0, metres: null }; // too narrow for even one — not issuable, Planning must look
  if (fitUps >= plannedUps) return { fitUps: plannedUps, metres: req }; // full width worth still fits; wider is just trim
  return { fitUps, metres: req * (plannedUps / fitUps) };
}

// Local calendar date (toISOString() would give yesterday between 00:00 and 05:30 IST).
export const today = () => new Date().toLocaleDateString("en-CA");

/** Whole days past a due date (YYYY-MM-DD…), or null when not overdue. */
export function daysLate(due) {
  if (!due) return null;
  const diff = Math.floor((Date.now() - new Date(`${String(due).slice(0, 10)}T00:00:00`).getTime()) / 86_400_000);
  return diff > 0 ? diff : null;
}

export const rollsPath = (q) => `/inventory?tab=rolls&q=${encodeURIComponent(q)}`;

/** A roll number or item code that opens the Rolls tab searched for it (plain text without Inventory access). */
export function RollsLink({ q, inline = false, className = "", children }) {
  const { user } = useAuth();
  if (!q) return <span className={className}>—</span>;
  if (!canAccess(user, "/inventory")) return <span className={className}>{children ?? q}</span>;
  return (
    <Link to={rollsPath(q)} title={`Rolls for ${q}`} onClick={(e) => e.stopPropagation()}
      className={`${inline ? "" : "block"} hover:text-[#FF5A00] hover:underline underline-offset-2 ${className}`}>
      {children ?? q}
    </Link>
  );
}

export function Kpi({ label, value, sub, icon: Icon, tone, testid }) {
  const cls =
    tone === "danger" ? "text-rose-700"
    : tone === "warn" ? "text-amber-700"
    : tone === "ok" ? "text-emerald-700"
    : "text-neutral-900";
  return (
    <div className="border border-neutral-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="label-caps text-[10px]">{label}</div>
        {Icon && <Icon size={15} strokeWidth={1.75} className="text-neutral-400 shrink-0" />}
      </div>
      <div className={`font-mono-num text-2xl font-bold mt-2 tracking-tight ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Panel({ title, right, children, className = "", testid }) {
  return (
    <section className={`border border-neutral-200 bg-white ${className}`} data-testid={testid}>
      {title && (
        <div className="px-5 py-3.5 border-b border-neutral-200 flex justify-between items-center gap-3">
          <div className="label-caps">{title}</div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/** Segmented control, same look as the Orders inquiries/orders switcher. */
export function SubTabs({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-1 border border-neutral-200 bg-white p-1 rounded-sm w-fit" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-4 py-1.5 text-sm rounded-sm font-medium transition-colors ${
            value === o.value ? "bg-[#FF5A00] text-white" : "text-neutral-600 hover:bg-neutral-100"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, value, onChange, testid, type = "text", placeholder, disabled, list, className = "" }) {
  return (
    <div className={className}>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input
        data-testid={testid}
        type={type}
        list={list}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="rounded-sm h-8 text-sm"
      />
    </div>
  );
}

const PILL = {
  ORDER: "bg-rose-50 text-rose-800 border-rose-200",
  OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  OPENING: "bg-neutral-50 text-neutral-600 border-neutral-200",
  PENDING: "bg-amber-50 text-amber-800 border-amber-200",
  "NOT APPROVED": "bg-rose-50 text-rose-800 border-rose-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "SLOW MOVING": "bg-amber-50 text-amber-800 border-amber-200",
  "NON MOVING": "bg-rose-50 text-rose-800 border-rose-200",
  to_issue: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-blue-50 text-blue-800 border-blue-200",
  issued: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const PILL_LABEL = {
  ORDER: "Reorder", OPENING: "Opening", PENDING: "QC pending", "NOT APPROVED": "Rejected",
  to_issue: "To issue", partial: "Part issued", issued: "Issued",
};

/** Current fiscal year as "26-27" (April–March). */
export function currentFy() {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(start % 100).padStart(2, "0")}-${String((start + 1) % 100).padStart(2, "0")}`;
}

export function previousFy(fy) {
  const start = Number(fy.slice(0, 2)) - 1;
  return `${String((start + 100) % 100).padStart(2, "0")}-${fy.slice(0, 2)}`;
}

export function Pill({ value }) {
  if (!value) return <span className="text-neutral-400">—</span>;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest border whitespace-nowrap ${PILL[value] || PILL.OPENING}`}>
      {PILL_LABEL[value] || value.toLowerCase()}
    </span>
  );
}

export function EmptyRow({ cols, loading, children }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-12 text-center text-neutral-400 text-sm">
        {loading ? "Loading…" : children}
      </td>
    </tr>
  );
}

export function Pager({ skip, limit, total, onChange }) {
  if (total <= limit) return null;
  const page = Math.floor(skip / limit);
  const last = Math.ceil(total / limit) - 1;
  return (
    <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between text-sm text-neutral-600">
      <span className="font-mono-num text-xs">
        {(skip + 1).toLocaleString("en-IN")}–{Math.min(total, skip + limit).toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-sm" disabled={page === 0} onClick={() => onChange((page - 1) * limit)}>
          Previous
        </Button>
        <span className="font-mono-num text-xs text-neutral-500">{page + 1} / {last + 1}</span>
        <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= last} onClick={() => onChange((page + 1) * limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}

/** Searchable roll picker, server-side, oldest stock first. `available` limits to rolls holding stock. */
export function RollPicker({ value, onPick, available = true, issuable = false, testid, placeholder = "Search roll number" }) {
  const [text, setText] = useState(value || "");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => setText(value || ""), [value]);
  useEffect(() => {
    if (!open || text.trim().length < 2) {
      setHits([]);
      return undefined;
    }
    const id = ++seq.current;
    const timer = setTimeout(() => {
      api
        .get("/stores/rolls", { params: { q: text.trim(), available, issuable, limit: 15 } })
        .then(({ data }) => id === seq.current && setHits(data.items))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [text, open, available, issuable]);

  return (
    <div className="relative">
      <Input
        data-testid={testid}
        value={text}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setText(e.target.value); onPick(null); }}
        className="rounded-sm h-8 text-sm font-mono-num"
      />
      {open && hits.length > 0 && (
        <div className="absolute z-50 mt-1 w-[26rem] max-w-[80vw] max-h-64 overflow-auto border border-neutral-200 bg-white shadow-md">
          {hits.map((r) => (
            <button
              type="button"
              key={r.roll_no}
              onMouseDown={() => { setText(r.roll_no); onPick(r); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-neutral-100 last:border-0"
            >
              <div className="flex justify-between gap-2">
                <span className="font-mono-num text-xs font-semibold">{r.roll_no}</span>
                <span className="font-mono-num text-xs">{qty(r.balance)} {unit(r.uom)}</span>
              </div>
              <div className="flex justify-between gap-2 text-[11px] text-neutral-500">
                <span className="truncate">{r.item_code} · {r.item_name}</span>
                <Pill value={r.qc_status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
